import React, { useEffect, useState, useCallback, useRef } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { fetchLikes, toggleLike } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * LikeButton — toggles a per-user like on a dossier. One like max per user,
 * enforced by a unique compound index on the backend. The button is disabled
 * while a request is in flight so spam-tapping cannot inflate the count.
 */
export const LikeButton = ({ slug }) => {
  const { user, token, login } = useAuth();
  const [count, setCount] = useState(null);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await fetchLikes(slug, token);
      setCount(r.like_count);
      setLiked(!!r.liked_by_me);
    } catch {
      setCount(null);
    }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const onClick = useCallback(async () => {
    if (inFlight.current) return;
    if (!user) {
      try { sessionStorage.setItem("archive_post_login_path",
          window.location.pathname + window.location.search); } catch { /* noop */ }
      login();
      return;
    }
    inFlight.current = true;
    setBusy(true);
    // Optimistic
    setLiked((p) => !p);
    setCount((c) => (c == null ? c : c + (liked ? -1 : 1)));
    try {
      const r = await toggleLike(slug, token);
      setLiked(!!r.liked);
      setCount(r.like_count);
    } catch {
      toast.error("Couldn't register like — try again.");
      // Reconcile by re-fetching
      load();
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  }, [user, token, slug, liked, login, load]);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="like-button"
      aria-pressed={liked}
      aria-label={liked ? "Unlike" : "Like"}
      className={
        "inline-flex items-center gap-1.5 sm:gap-2 min-h-[44px] sm:min-h-0 px-1 sm:px-3 sm:py-2 border-0 sm:border " +
        "font-mono text-sm sm:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.25em] " +
        "transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
        (liked
          ? "text-[#D92D20] sm:border-[#D92D20] sm:bg-[#1a0808]"
          : "text-[#A39E93] hover:text-[#D92D20] sm:border-[#454038] sm:hover:border-[#D92D20]")
      }
    >
      <Heart
        size={14}
        className={liked ? "fill-[#D92D20] text-[#D92D20]" : ""}
        data-testid="like-icon"
      />
      <span data-testid="like-count">
        {count == null ? "—" : count}<span className="hidden sm:inline"> {liked ? "liked" : "like"}</span>
      </span>
    </button>
  );
};

export default LikeButton;
